import {i18n} from '@online-offer/i18n';
import {Hide, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {always, isNotNil} from 'ramda';

import {Nullish, parseDate} from 'shared/web';

import {AlternativeChoiceCard} from '../../components/AlternativeChoiceCard/AlternativeChoiceCard';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {useViewedAlternative} from '../../hooks/useViewedAlternative';
import {ReservationCard} from '../ReservationCard/ReservationCard';
import {SellerCard} from '../SellerCard/SellerCard';
import {TestDriveCard} from '../TestDriveCard/TestDriveCard';
import {TimerCard, TimerCardColorSchema, TimerState, TimerStateType} from '../TimerCard/TimerCard';
import {getOfferValidityDeadline} from './getOfferValidityDeadline';

const getTimerState = ({
  days,
  hours,
  minutes,
  seconds,
}: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}): TimerState => {
  if (days === 0 && hours < 2) {
    if (hours === 0 && minutes === 0 && seconds === 0) {
      return 'expired';
    } else {
      return 'soon_expired';
    }
  }
  return 'reserved';
};

const getTime = (deadline: string | Date | Nullish) => {
  if (!deadline) {
    return null;
  }
  const time = parseDate(deadline).getTime() - new Date().getTime(); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
  if (time <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }
  return {
    days: Math.floor(time / (1000 * 60 * 60 * 24)),
    hours: Math.floor((time / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((time / 1000 / 60) % 60),
    seconds: Math.floor((time / 1000) % 60),
  };
};

const getReservationColorSchema = (timerState: TimerState): TimerCardColorSchema =>
  match<TimerState, TimerCardColorSchema>(timerState)
    .with('reserved', always({cardBg: 'semantic.bg.green', textColor: 'successDark'}))
    .with('soon_expired', always({cardBg: 'severity.warningCard', textColor: 'warningDark'}))
    .with('expired', always({cardBg: 'severity.dangerCard', textColor: 'primary'}))
    .exhaustive();

const getOfferValidityColorSchema = (timerState: TimerState): TimerCardColorSchema =>
  match<TimerState, TimerCardColorSchema>(timerState)
    .with('reserved', always({cardBg: 'accent.lightest', textColor: 'accentDarker'}))
    .with('soon_expired', always({cardBg: 'severity.warningCard', textColor: 'warningDark'}))
    .with('expired', always({cardBg: 'severity.dangerCard', textColor: 'primary'}))
    .exhaustive();

const getOfferValidityLabel = (timerState: TimerState): string =>
  match<TimerState, string>(timerState)
    .with('expired', always(i18n.t('navigation.offerValidity')))
    .with('reserved', 'soon_expired', always(i18n.t('timerCard.offerValidity')))
    .exhaustive();

export function Sidebar() {
  const {activeReservation, offerValidTo} = useGetOnlineOfferData();
  // The deal vehicle's own alternative: the customer can choose it right here.
  const {activeAlternative} = useViewedAlternative();

  const initialReservationTime = getTime(activeReservation?.reservedUntilAt);
  const [reservationTimer, setReservationTimer] = useState<TimerStateType | null>(
    initialReservationTime
      ? {...initialReservationTime, state: getTimerState(initialReservationTime)}
      : null
  );

  const initialOfferValidityTime = getTime(getOfferValidityDeadline(offerValidTo));
  const [offerValidityTimer, setOfferValidityTimer] = useState<TimerStateType | null>(
    initialOfferValidityTime
      ? {...initialOfferValidityTime, state: getTimerState(initialOfferValidityTime)}
      : null
  );

  useEffect(
    function tickReservationTimer() {
      const interval = setInterval(() => {
        const time = getTime(activeReservation?.reservedUntilAt);
        if (!time) {
          return;
        }
        const timerState = getTimerState(time);
        setReservationTimer({...time, state: timerState});
        if (time.days === 0 && time.hours === 0 && time.minutes === 0 && time.seconds === 0) {
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    },
    [activeReservation?.reservedUntilAt]
  );

  useEffect(
    function tickOfferValidityTimer() {
      const interval = setInterval(() => {
        const time = getTime(getOfferValidityDeadline(offerValidTo));
        if (!time) {
          return;
        }
        const timerState = getTimerState(time);
        setOfferValidityTimer({...time, state: timerState});
        if (time.days === 0 && time.hours === 0 && time.minutes === 0 && time.seconds === 0) {
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    },
    [offerValidTo]
  );

  const reservationColorSchema = isNotNil(reservationTimer)
    ? getReservationColorSchema(reservationTimer.state)
    : undefined;

  const offerValidityColorSchema = isNotNil(offerValidityTimer)
    ? getOfferValidityColorSchema(offerValidityTimer.state)
    : undefined;

  const offerValidityLabel = isNotNil(offerValidityTimer)
    ? getOfferValidityLabel(offerValidityTimer.state)
    : undefined;

  return (
    <>
      <VStack spacing={2}>
        <Show when={isNotNil(offerValidityTimer) && isNotNil(offerValidityColorSchema)}>
          <TimerCard
            timer={offerValidityTimer!}
            reservedUntil={offerValidTo ?? ''}
            colorSchema={offerValidityColorSchema!}
            label={offerValidityLabel}
          />
        </Show>
        <Show when={isNotNil(reservationTimer) && isNotNil(reservationColorSchema)}>
          <TimerCard
            timer={reservationTimer!}
            reservedUntil={activeReservation?.reservedUntilAt ?? ''}
            colorSchema={reservationColorSchema!}
            dateFormat="dateTimeShort"
          />
        </Show>
      </VStack>
      <TestDriveCard />
      <ReservationCard isExpired={reservationTimer?.state === 'expired'} />
      <AlternativeChoiceCard alternative={activeAlternative} />
      <Hide onMobile onTablet>
        <SellerCard />
      </Hide>
    </>
  );
}
